// Competitor comparison data for /splitwise-alternative.
//
// ─── RULES FOR EDITING THIS FILE ────────────────────────────────────────────
// Every Splitwise claim here is quoted from Splitwise's own public pages, with
// the source and the date it was checked recorded below. That is deliberate:
// a comparison page that misstates a competitor is a trust problem and a legal
// one, and "I remembered it that way" is not a defence.
//
// So: do not add a row unless you have just looked at the live page and can
// point at the words. If a claim can't be sourced, it belongs in
// UNVERIFIED_CLAIMS at the bottom, not in the table.
//
// Feature sets change. Re-check before any campaign that drives traffic here,
// and bump VERIFIED_ON so the page's own "checked on" line stays honest.
// ────────────────────────────────────────────────────────────────────────────

// Displayed on the page, so readers can judge how fresh the comparison is.
export const VERIFIED_ON = '2026-08-01';
export const SOURCES = [
  { label: 'splitwise.com/pro', url: 'https://www.splitwise.com/pro' },
  { label: 'splitwise.com', url: 'https://www.splitwise.com/' },
];

export type Row = {
  feature: string;
  // What Tabster does. Sourced from our own product — safe to assert.
  tabster: string;
  // Does the Tabster cell get the tick and the ombre? Set explicitly, because
  // the previous version inferred it from tabster.startsWith('Free') and that
  // silently dropped the tick the moment the copy changed to "No ads".
  tabsterYes?: boolean;
  // What Splitwise's own page says. Quote or close paraphrase only.
  splitwise: string;
  // Same tick treatment on the Splitwise cell, for features they also give away
  // free. Marking their wins the same way ours are marked is the point — a
  // comparison where only one column can earn a tick isn't one.
  splitwiseYes?: boolean;
  // Pulls the eye to the rows where the difference is the reason to switch.
  highlight?: boolean;
};

// Rows below compare where a feature sits in each product's PRICING TIER —
// not whether it exists. That's the honest axis: Splitwise has receipt
// scanning, it's just listed under Pro. Claiming they "don't have it" would be
// false, and readers who know the product would catch it instantly.
// One spelling of the Pro cell, referenced everywhere, so a typo can't quietly
// drop a row out of the shaded set below.
const PRO = 'Pro subscription';

const ROWS: Row[] = [
  // Opens on the thing both apps do for free. Leading with a tie rather than a
  // win reads as a fair comparison rather than a pitch, and it sets the
  // baseline the paid rows below are measured against.
  {
    feature: 'Inputting expenses to track',
    tabster: 'Free',
    tabsterYes: true,
    splitwise: 'Free',
    splitwiseYes: true,
  },
  { feature: 'Scanning a receipt', tabster: 'Free', tabsterYes: true, splitwise: PRO },
  { feature: 'Itemizing a bill line by line', tabster: 'Free', tabsterYes: true, splitwise: PRO },
  { feature: 'Unlimited expenses', tabster: 'Free', tabsterYes: true, splitwise: PRO },
  { feature: 'Currency conversion', tabster: 'Free', tabsterYes: true, splitwise: PRO },
  { feature: 'Charts and graphs', tabster: 'Not offered', splitwise: PRO },
  { feature: 'Expense search', tabster: 'Free', tabsterYes: true, splitwise: PRO },
  { feature: 'An ad-free experience', tabster: 'No ads', tabsterYes: true, splitwise: PRO },
  // The one ABSENCE-based row on the table, so it's worded narrowly on purpose.
  // Splitwise's own homepage feature list (fetched 2026-08-01) runs: groups and
  // friends, splits by %/shares, balances, simplify debts, recurring expenses,
  // offline mode, cloud sync, spending totals, categories, languages,
  // currencies, payment integrations — no feed, photos or stories anywhere.
  //
  // A vaguer label like "social features" would NOT be defensible: they do have
  // comments on expenses, so someone could fairly call that social and the row
  // false. "A social feed with photos" is the thing they demonstrably do not
  // ship. Keep it that specific.
  { feature: 'A social feed with photos', tabster: 'Free', tabsterYes: true, splitwise: 'Not offered' },
];

// Shade every row where the difference IS the argument: Tabster gives it away,
// Splitwise puts it behind Pro. Derived from the data rather than hand-set —
// `highlight: true` was originally typed onto three rows and then silently went
// stale as more rows were added, so the shading stopped matching its own rule.
//
// Deliberately excluded, though both have a Pro cell:
//   Charts and graphs — Tabster doesn't offer it, so shading it would flag a
//     row we lose as though it were a win.
//   A social feed with photos — Splitwise doesn't gate it behind Pro, they
//     don't ship it, which is a different claim.
export const COMPARISON: Row[] = ROWS.map((row) => ({
  ...row,
  highlight: !!row.tabsterYes && row.splitwise === PRO,
}));

// Things Splitwise's own homepage advertises on its free tier. This section is
// not a concession — it is the reason the page is worth reading. A comparison
// that pretends the competitor has no strengths reads as marketing and gets
// treated as such by readers and by Google. Someone who lands here deciding
// between two apps is better served by the truth, and more likely to trust the
// rest of the page because of it.
// Each entry is a Splitwise free-tier claim (quoted from their homepage) plus
// the specific Tabster limitation behind it. Naming our own gap in the same
// breath is what makes the section credible — a vague "they're good at some
// things" reads as a hedge; "Tabster only supports English" reads as true.
//
// Two earlier entries were removed on purpose, not lost:
//   Offline mode — Tabster's is being brought up to par, so conceding it as a
//     permanent Splitwise advantage would be wrong by the time anyone reads it.
//   Simplify debts across a large group — Tabster does this; it nets the whole
//     trip down per pair. It should never have been on this list.
export const SPLITWISE_STRENGTHS = [
  'Support for 100+ currencies and 7+ languages — while Tabster supports currency conversion on receipts to USD, Tabster only supports English language.',
  'Spending totals and expense categories — Tabster does not help you categorize your expenses.',
];

// Where Tabster is genuinely different. These are OUR claims about OUR product,
// verifiable from the app itself — no competitor assertion involved, so they
// carry no sourcing risk.
// The emoji is decorative — it repeats what the title already says, so it is
// rendered aria-hidden and a screen reader never announces "receipt emoji"
// ahead of the heading.
export const TABSTER_DIFFERENCES: Array<{ emoji: string; title: string; body: string }> = [
  {
    emoji: '🧾',
    title: 'The receipt itemizes itself for free',
    body: 'Take a photo and every line item comes back priced and ready to claim. Nobody types the bill in.',
  },
  {
    emoji: '👆',
    title: 'Everyone claims at once, at the table',
    body: 'The receipt is live. Your friends tap what they ordered on their own phones and you watch the totals move in real time, instead of one person reconstructing the meal afterwards.',
  },
  {
    emoji: '🔗',
    title: 'Friends do not need an account',
    body: 'Send a link or let them scan the QR code. They claim their items and pay without downloading anything or signing up.',
  },
  {
    emoji: '✅',
    title: 'Tax and tip split themselves',
    body: 'Surcharges are apportioned to what each person actually claimed, so nobody overpays fees on a dish they did not order.',
  },
  {
    emoji: '💸',
    title: 'Payment is one tap',
    body: 'Venmo, PayPal and Cash App open with the amount and the recipient already filled in.',
  },
];

// ─── NOT PUBLISHED — verify before promoting any of these to COMPARISON ─────
// Plausible differentiators that Splitwise's public pages did not state either
// way on 2026-08-01. Absence of a claim on a marketing page is NOT evidence the
// feature is missing, so none of these are on the page.
export const UNVERIFIED_CLAIMS = [
  'Whether Splitwise requires an account to record or settle an expense',
  'Whether Splitwise offers real-time collaborative claiming',
  'The price of Splitwise Pro (no price was shown on splitwise.com/pro)',
  'Any free-tier expense cap implied by "Unlimited expenses" being a Pro feature',
];
